import { apiClient } from "./apiClient";
import i18n from "@/utils/i18n";

const ENDPOINT = "/api/admin/users";

/** Location object from API */
export interface AdminUserLocation {
  id: string;
  name: string;
  address?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Company object from API */
export interface AdminUserCompany {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Role item from API */
export interface AdminUserRole {
  id: string;
  domainID?: string;
  name: string;
  type?: number;
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Department object from API (when nested) */
export interface AdminUserDepartment {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

/** GET /api/admin/users - list item (matches current API response) */
export interface AdminUserListItem {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  identityNumber?: string;
  phoneNumber?: string;
  ldapUsername?: string | null;
  title?: string | null;
  managerID?: string | null;
  isActive?: boolean;
   /** Kullanıcı banlı mı? (isBanned: true => Eğitim'e kaydet kapalı) */
  isBanned?: boolean;
  phoneVerified?: boolean;
  createdAt?: string;
  updatedAt?: string;
  type?: number;
  /** Nested objects from API */
  location?: AdminUserLocation | null;
  company?: AdminUserCompany | null;
  department?: AdminUserDepartment | string | null;
  /** Bağlı olduğu kök Başkanlık / İşletme (sınıf oluşturma ekranı için). */
  rootDepartment?: AdminUserDepartment | null;
  roles?: AdminUserRole[];
  /** Optional flat fields if backend returns them */
  completedTrainings?: number;
  /** Yeni istatistik alanları (backend response ile uyumlu) */
  completedEducationsCount?: number;
  takenEducationsCount?: number;
  certificatesCount?: number;
  absencesCount?: number;
}

/** GET /api/admin/users/:id - single user (same or extended shape) */
export type AdminUserDetail = AdminUserListItem;

/** PATCH /api/admin/users/:id - partial update body */
export interface AdminUserUpdateBody {
  firstName?: string;
  lastName?: string;
  email?: string;
  identityNumber?: string;
  phoneNumber?: string;
  status?: string;
  title?: string;
  ldapUsername?: string;
  /** Eski string alanlar (geriye dönük uyumluluk için) */
  department?: string;
  company?: string;
  location?: string;
  /** İlgili tabloların UUID alanları */
  departmentId?: string;
  companyId?: string;
  locationId?: string;
  /** Cinsiyet (backend: male / female) */
  gender?: "male" | "female";
  /** Yönetici: kullanıcı şifresi güncelleme */
  password?: string;
}

/** POST /api/admin/users - create user body (Swagger CreateUserDto) */
export interface AdminUserCreateBody {
  domainId?: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  companyId?: string;
  locationId?: string;
  password?: string;
  email?: string;
  identityNumber?: string;
  ldapUsername?: string;
  title?: string;
  departmentId?: string;
  managerID?: string;
  roleId?: string;
  /** Cinsiyet (backend: male / female) */
  gender?: "male" | "female";
}

/** User role (GET /api/admin/users/:id/roles) */
export interface UserRoleItem {
  id: string;
  name?: string;
  [key: string]: unknown;
}

/** GET /api/admin/users - list response */
export interface AdminUsersListResponse {
  /** Primary array field (some backends use this) */
  data?: AdminUserListItem[];
  /** Alternative array field (some backends use `items`) */
  items?: AdminUserListItem[];
  count?: number;
  total?: number;
}

export interface AdminUsersListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  roleId?: string;
  role?: string;
  roleType?: number;
  locationId?: string;
  companyId?: string;
  departmentId?: string;
  /** Kök birim filtresi — backend subtree çözümü uygular. */
  rootDepartmentId?: string;
  /** Ünvan substring filtresi (case-insensitive). */
  title?: string;
  /** TC No substring filtresi (kolon-bazlı). */
  identityNumber?: string;
  /** Ad/Soyad substring filtresi (kolon-bazlı). */
  name?: string;
  /** Tam giriş tarihi filtresi (ISO: YYYY-MM-DD). */
  joinedAt?: string;
  /** En az kıdem yılı (integer, 0-80). */
  minTenureYears?: number;
  groupId?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * GET /api/admin/users - List users with pagination and search.
 * Top-level managers see all users; others see only subordinates.
 */
export async function getAdminUsers(
  params?: AdminUsersListParams,
): Promise<{ data: AdminUserListItem[]; total: number }> {
  const query: Record<string, unknown> = {};
  if (params?.page != null) query.page = params.page;
  if (params?.limit != null) query.limit = params.limit;
  if (params?.search) query.search = params.search;
  if (params?.status) query.status = params.status;
  if (params?.roleId) query.roleId = params.roleId;
  if (params?.role) query.role = params.role;
  if (params?.roleType !== undefined) query.roleType = params.roleType;
  if (params?.locationId) query.locationId = params.locationId;
  if (params?.companyId) query.companyId = params.companyId;
  if (params?.departmentId) query.departmentId = params.departmentId;
  if (params?.rootDepartmentId) query.rootDepartmentId = params.rootDepartmentId;
  if (params?.title) query.title = params.title;
  if (params?.identityNumber) query.identityNumber = params.identityNumber;
  if (params?.name) query.name = params.name;
  if (params?.joinedAt) query.joinedAt = params.joinedAt;
  if (params?.minTenureYears != null) query.minTenureYears = params.minTenureYears;
  if (params?.groupId) query.groupId = params.groupId;
  if (params?.sortBy) query.sortBy = params.sortBy;
  if (params?.sortOrder) query.sortOrder = params.sortOrder;

  const { data } = await apiClient.get<AdminUsersListResponse>(ENDPOINT, {
    params: query,
  });
  const list = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data as unknown as AdminUserListItem[])
        ? (data as unknown as AdminUserListItem[])
        : [];
  const total = data?.count ?? data?.total ?? list.length;
  return { data: list, total };
}

/**
 * POST /api/admin/users/by-ids - Batch fetch.
 * Birden fazla user ID'yi tek atımda getir; domain dışı / soft-deleted ID'ler
 * sessizce atılır. Loop yerine bu kullanılmalı.
 */
export async function getAdminUsersByIds(
  ids: string[],
): Promise<AdminUserListItem[]> {
  if (ids.length === 0) return [];
  const { data } = await apiClient.post<{
    items?: AdminUserListItem[];
    data?: AdminUserListItem[];
    count?: number;
  }>(`${ENDPOINT}/by-ids`, { ids });
  const items = data?.items ?? data?.data;
  return Array.isArray(items) ? items : [];
}

/**
 * GET /api/admin/users/:id - Get single user by ID (Admin only).
 */
export async function getAdminUserById(
  id: string,
): Promise<AdminUserDetail> {
  const { data } = await apiClient.get<{ data?: AdminUserDetail }>(
    `${ENDPOINT}/${id}`,
  );
  if (!data?.data) throw new Error("User not found");
  return data.data;
}

/**
 * PATCH /api/admin/users/:id - Update user partial (Admin only).
 */
export async function updateAdminUser(
  id: string,
  body: AdminUserUpdateBody,
): Promise<AdminUserDetail> {
  const { data } = await apiClient.patch<{ data?: AdminUserDetail }>(
    `${ENDPOINT}/${id}`,
    body,
  );
  if (!data?.data) throw new Error("Update failed");
  return data.data;
}

/**
 * PUT /api/admin/users/:id/password - Update user's password (Admin only).
 */
export async function updateAdminUserPassword(
  id: string,
  body: { password: string },
): Promise<unknown> {
  const { data } = await apiClient.put<unknown>(`${ENDPOINT}/${id}/password`, body);
  return data;
}

/**
 * POST /api/admin/users - Create new user (Admin only).
 */
export async function createAdminUser(
  body: AdminUserCreateBody,
): Promise<AdminUserDetail> {
  const { data } = await apiClient.post<{ data?: AdminUserDetail }>(
    ENDPOINT,
    body,
    // Bu çağrının catch bloğu kendisi Türkçeye çevrilmiş özel toast
    // gösteriyor; interceptor'ın global toast'ını bastır.
    { skipErrorToast: true },
  );
  if (!data?.data) throw new Error(i18n.t("usersApi.createFailed"));
  return data.data;
}

/**
 * DELETE /api/admin/users/:id - Soft-delete user (Admin only).
 */
export async function deleteAdminUser(id: string): Promise<void> {
  await apiClient.delete(`${ENDPOINT}/${id}`);
}

/**
 * POST /api/admin/users/bulk-import - Bulk import users from Excel (Admin only).
 */
export async function bulkImportAdminUsers(
  file: File,
): Promise<{ data?: { imported?: number; errors?: unknown[] } }> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post<{
    data?: { imported?: number; errors?: unknown[] };
  }>(`${ENDPOINT}/bulk-import`, formData);
  return data;
}

/**
 * GET /api/admin/users/:id/roles - Get user's roles.
 */
export async function getUserRoles(
  userId: string,
): Promise<UserRoleItem[]> {
  const { data } = await apiClient.get<{ data?: UserRoleItem[] }>(
    `${ENDPOINT}/${userId}/roles`,
  );
  return data?.data ?? [];
}

/**
 * PUT /api/admin/users/:id/roles - Set user roles (full replace). Used for both new assignments and updates.
 */
export async function assignUserRole(
  userId: string,
  roleIds: string[],
): Promise<unknown> {
  const { data } = await apiClient.put<unknown>(
    `${ENDPOINT}/${userId}/roles`,
    { roleIds },
  );
  return data;
}

/**
 * DELETE /api/admin/users/:id/roles/:roleId - Revoke role from user (Admin only).
 */
export async function revokeUserRole(
  userId: string,
  roleId: string,
): Promise<void> {
  await apiClient.delete(`${ENDPOINT}/${userId}/roles/${roleId}`);
}

/** GET /api/admin/locations - list item */
export interface AdminLocationItem {
  id: string;
  name: string;
  address?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** GET /api/admin/companies - list item */
export interface AdminCompanyItem {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * GET /api/admin/locations - List locations for filters.
 */
export async function getAdminLocations(): Promise<AdminLocationItem[]> {
  const { data } = await apiClient.get<{
    data?: AdminLocationItem[];
    count?: number;
  }>("/api/admin/locations");
  return data?.data ?? [];
}

/**
 * GET /api/admin/companies - List companies for filters.
 */
export async function getAdminCompanies(): Promise<AdminCompanyItem[]> {
  const { data } = await apiClient.get<{
    data?: AdminCompanyItem[];
    count?: number;
  }>("/api/admin/companies");
  return data?.data ?? [];
}

/** GET /api/admin/departments - list item */
export interface AdminDepartmentItem {
  id: string;
  name: string;
  type?: number;
}

/**
 * GET /api/admin/departments - List departments for filters.
 * Backend max limit=100; tüm birimleri çekmek için sayfa sayfa iterate et.
 */
export async function getAdminDepartments(): Promise<AdminDepartmentItem[]> {
  const LIMIT = 100;
  const MAX_PAGES = 100;
  const all: AdminDepartmentItem[] = [];
  let total: number | null = null;

  for (let page = 1; page <= MAX_PAGES; page++) {
    const { data } = await apiClient.get<{
      data?:
        | AdminDepartmentItem[]
        | { items?: AdminDepartmentItem[]; total?: number; count?: number };
      items?: AdminDepartmentItem[];
      count?: number;
      total?: number;
    }>("/api/admin/departments", { params: { page, limit: LIMIT } });

    let items: AdminDepartmentItem[] = [];
    if (Array.isArray(data?.data)) {
      items = data.data;
    } else if (data?.data && typeof data.data === "object") {
      const d = data.data as {
        items?: AdminDepartmentItem[];
        total?: number;
        count?: number;
      };
      items = d.items ?? [];
      if (total == null) total = d.total ?? d.count ?? null;
    } else if (Array.isArray(data?.items)) {
      items = data.items;
      if (total == null) total = data.count ?? data.total ?? null;
    }

    all.push(...items);
    if (total != null && all.length >= total) break;
    if (items.length < LIMIT) break;
  }
  return all;
}

/**
 * GET /api/admin/departments/roots - List only root (parentID IS NULL) departments
 * for personnel listing dropdown (kök Başkanlık / Santral / İşletme).
 */
export async function getAdminRootDepartments(): Promise<AdminDepartmentItem[]> {
  const { data } = await apiClient.get<{
    data?: AdminDepartmentItem[];
    items?: AdminDepartmentItem[];
  }>("/api/admin/departments/roots");
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

/** GET /api/admin/domains - list item */
export interface AdminDomainItem {
  id: string;
  name?: string;
  [key: string]: unknown;
}

/**
 * GET /api/admin/domains - List domains for user creation (domainId required).
 * Fallback: if endpoint fails, returns companies as domain options.
 */
export async function getAdminDomains(): Promise<AdminDomainItem[]> {
  try {
    const { data } = await apiClient.get<{
      data?: AdminDomainItem[];
      items?: AdminDomainItem[];
    }>("/api/admin/domains");
    const list = Array.isArray(data?.data) ? data.data : data?.items ?? [];
    if (list.length > 0) return list;
  } catch {
    // Domains endpoint may not exist
  }
  // Fallback: use companies as domains (some backends use companyId as domainId)
  const companies = await getAdminCompanies();
  return companies.map((c) => ({ id: c.id, name: c.name }));
}
